import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { ApiError, customerApi } from '@/lib/pillarApi';
import type { MerchantCustomerRecordDetailsResponse } from '@pillar-portal/api-types';
import type { Status } from '@pillar-portal/database/dist/frontend';

const CUSTOMER_STATUSES: readonly Status[] = ['PENDING', 'ACTIVE', 'DISABLED', 'DELETED'];

function parseCustomerStatus(value: string): Status {
    const matchingStatus = CUSTOMER_STATUSES.find((statusOption) => statusOption === value);
    if (matchingStatus === undefined) {
        throw new Error(`Unsupported customer status: ${value}`);
    }
    return matchingStatus;
}

function CustomerDetailPageContent() {
    const params = useParams();
    const merchantCustomerRecordUuid = params.uuid;
    const queryClient = useQueryClient();
    const [name, setName] = useState<string>('');
    const [primaryEmail, setPrimaryEmail] = useState<string>('');
    const [status, setStatus] = useState<Status>('PENDING');
    const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);

    if (merchantCustomerRecordUuid === undefined || merchantCustomerRecordUuid.trim().length === 0) {
        throw new Error('Missing merchant customer record UUID');
    }

    const customerQuery = useQuery<MerchantCustomerRecordDetailsResponse>({
        queryKey: ['merchantCustomerDetails', merchantCustomerRecordUuid],
        queryFn: async () => await customerApi.getCustomer(merchantCustomerRecordUuid),
        retry: false,
    });

    useEffect(() => {
        if (customerQuery.data === undefined) {
            return;
        }
        setName(customerQuery.data.name);
        setPrimaryEmail(customerQuery.data.primary_email ?? '');
        setStatus(customerQuery.data.status);
    }, [customerQuery.data]);

    const updateCustomerMutation = useMutation({
        mutationFn: async () =>
            await customerApi.updateCustomer(merchantCustomerRecordUuid, {
                name,
                primary_email: primaryEmail.length > 0 ? primaryEmail : undefined,
                status,
            }),
        onSuccess: async (updatedCustomer) => {
            setSaveSuccessMessage('Customer updated.');
            queryClient.setQueryData(
                ['merchantCustomerDetails', merchantCustomerRecordUuid],
                updatedCustomer
            );
            await queryClient.invalidateQueries({ queryKey: ['merchantCustomers'] });
        },
    });

    if (customerQuery.isLoading) {
        return <div>Loading customer details...</div>;
    }

    if (customerQuery.error !== null || customerQuery.data === undefined) {
        const errorMessage =
            customerQuery.error instanceof ApiError
                ? customerQuery.error.message
                : 'Failed to load customer details.';
        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load customer</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const customer = customerQuery.data;

    return (
        <div className="space-y-6">
            <div className="space-y-1">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold">{customer.name}</h1>
                    <StatusBadge status={customer.status} />
                </div>
                <p className="text-sm text-muted-foreground">
                    Merchant customer record details
                </p>
            </div>

            {updateCustomerMutation.isError ? (
                <Alert variant="destructive">
                    <AlertTitle>Unable to update customer</AlertTitle>
                    <AlertDescription>
                        {updateCustomerMutation.error instanceof ApiError
                            ? updateCustomerMutation.error.message
                            : 'Please try again.'}
                    </AlertDescription>
                </Alert>
            ) : null}

            {saveSuccessMessage !== null ? (
                <Alert>
                    <AlertTitle>Saved</AlertTitle>
                    <AlertDescription>{saveSuccessMessage}</AlertDescription>
                </Alert>
            ) : null}

            <Card>
                <CardHeader>
                    <CardTitle>Customer Record</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="customer-name">Name</Label>
                        <Input
                            id="customer-name"
                            value={name}
                            onChange={(event) => {
                                setSaveSuccessMessage(null);
                                setName(event.target.value);
                            }}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="customer-primary-email">Primary Email</Label>
                        <Input
                            id="customer-primary-email"
                            value={primaryEmail}
                            onChange={(event) => {
                                setSaveSuccessMessage(null);
                                setPrimaryEmail(event.target.value);
                            }}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="customer-status">Status</Label>
                        <select
                            id="customer-status"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={status}
                            onChange={(event) => {
                                setSaveSuccessMessage(null);
                                setStatus(parseCustomerStatus(event.target.value));
                            }}
                        >
                            {CUSTOMER_STATUSES.map((statusOption) => (
                                <option key={statusOption} value={statusOption}>
                                    {statusOption}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-muted-foreground">
                                Primary Phone
                            </p>
                            <p className="text-sm">{customer.primary_phone ?? '—'}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-muted-foreground">
                                Customer Organization
                            </p>
                            <p className="text-sm">
                                {customer.customer_organization_name ?? '—'}
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            onClick={() => updateCustomerMutation.mutate()}
                            disabled={updateCustomerMutation.isPending}
                        >
                            Save
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Customer Details Payload</CardTitle>
                </CardHeader>
                <CardContent>
                    <pre className="overflow-x-auto rounded-md bg-muted p-4 text-sm">
                        {JSON.stringify(customer.customer_details, null, 2)}
                    </pre>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CustomerDetailPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerDetailPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
